import Close from "@mui/icons-material/Close";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createWidgetsBatch, type BatchWidgetSpec } from "@/features/app-studio/api/appItemsApi";
import { useAddToAppTarget } from "@/features/app-studio/hooks/useAddToAppTarget";
import { AddToAppTargetSelect } from "@/features/app-studio/ui/AddToAppTargetSelect";
import { AppChartColumnSpanSelect } from "@/features/app-studio/ui/AppChartColumnSpanSelect";
import { APP_KPI_DEFAULT_COLUMN_SPAN } from "@/features/app-studio/lib/appChartLayout";
import type { CardDefinition } from "@/features/chat/model/chatTypes";

type AddCardsToAppDialogProps = {
  open: boolean;
  onClose: () => void;
  cards: CardDefinition[];
  sql: string | null;
};

/**
 * Adds one or more chat response cards to an app as KPI widgets. Every selected
 * card is created in a single batch so they all share one saved query, which then
 * executes once per app refresh.
 */
export function AddCardsToAppDialog({ open, onClose, cards, sql }: AddCardsToAppDialogProps) {
  const { t } = useTranslation("appStudio");
  const navigate = useNavigate();
  const target = useAddToAppTarget(open);
  const { reset: resetTarget, selectedAppId, tabIdForPayload, isTargetComplete } = target;

  // Tracked as exclusions rather than inclusions: every card of the turn is added by
  // default, and the list only records the ones the user unticked.
  const [excludedCardIds, setExcludedCardIds] = useState<string[]>([]);
  const [columnSpan, setColumnSpan] = useState<number>(APP_KPI_DEFAULT_COLUMN_SPAN);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    resetTarget();
    setExcludedCardIds([]);
    setColumnSpan(APP_KPI_DEFAULT_COLUMN_SPAN);
    setIsSaving(false);
    setSaveError(null);
  }, [resetTarget]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const selectedCards = useMemo(
    () => cards.filter((card) => !excludedCardIds.includes(card.id)),
    [cards, excludedCardIds],
  );

  const toggleCard = (cardId: string) => {
    setExcludedCardIds((prev) =>
      prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId],
    );
    setSaveError(null);
  };

  const handleClose = () => {
    if (isSaving) {
      return;
    }
    onClose();
  };

  const handleConfirm = async () => {
    if (!sql || selectedAppId === "" || !isTargetComplete || selectedCards.length === 0) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const widgets: BatchWidgetSpec[] = selectedCards.map((card) => ({
        item_type: "kpi",
        name: card.title,
        value_column: card.column,
        format: card.format,
        column_span: columnSpan,
        tab_id: tabIdForPayload,
      }));
      await createWidgetsBatch(selectedAppId, { query: { sql }, widgets });
      onClose();
      navigate(`/app-studio/${selectedAppId}`, { state: { activeTabId: tabIdForPayload } });
    } catch (error) {
      const message = error instanceof Error ? error.message : t("addCards.errors.saveFailed");
      setSaveError(message);
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || isSaving) {
          return;
        }
        handleClose();
      }}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            bgcolor: "var(--theme-surface)",
            backgroundImage: "none",
            border: "1px solid var(--theme-border)",
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {t("addCards.title")}
        </Typography>
        <IconButton
          aria-label={t("addCards.closeAria")}
          onClick={handleClose}
          disabled={isSaving}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)" }}>
            {t("addCards.description")}
          </Typography>

          {!sql ? <Alert severity="warning">{t("addCards.noSql")}</Alert> : null}

          {cards.length > 1 ? (
            <FormControl component="fieldset" disabled={isSaving || !sql}>
              <FormLabel component="legend" sx={{ fontSize: "0.875rem" }}>
                {t("addCards.cardsLabel")}
              </FormLabel>
              <FormGroup>
                {cards.map((card) => (
                  <FormControlLabel
                    key={card.id}
                    control={
                      <Checkbox
                        checked={!excludedCardIds.includes(card.id)}
                        onChange={() => toggleCard(card.id)}
                      />
                    }
                    label={card.title}
                  />
                ))}
              </FormGroup>
              {selectedCards.length === 0 ? (
                <Alert severity="info">{t("addCards.errors.cardRequired")}</Alert>
              ) : null}
            </FormControl>
          ) : null}

          <AddToAppTargetSelect
            idPrefix="add-cards"
            target={target}
            disabled={isSaving || !sql}
            onTargetChange={() => setSaveError(null)}
          />

          <AppChartColumnSpanSelect
            value={columnSpan}
            disabled={isSaving || !sql}
            onChange={setColumnSpan}
          />

          {saveError ? (
            <Alert severity="error" icon={<WarningAmber fontSize="inherit" />}>
              {saveError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid var(--theme-border)" }}>
        <Button onClick={handleClose} disabled={isSaving} sx={{ textTransform: "none" }}>
          {t("addCards.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={isSaving || !sql || !isTargetComplete || selectedCards.length === 0}
          sx={{ textTransform: "none" }}
        >
          {isSaving ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} color="inherit" />
              <span>{t("addCards.adding")}</span>
            </Stack>
          ) : (
            t("addCards.confirm")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
